import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, CalendarClock, XCircle, AlertTriangle, ShieldCheck, Loader2, Eye } from "lucide-react";

/**
 * BookingActions — inline Cancel + Reschedule buttons for any booking
 * (1:1, Performance, Group, Assessment).
 *
 * Behaviour:
 *   1. On mount, hits /bookings/{id}/reschedule-info to get the effective
 *      cutoff (offering override → coach default → 48h) + hours-to-start
 *   2. Renders friendly buttons that adapt to the cutoff state:
 *        · inside window: normal action
 *        · outside window + coach/admin: yellow "override" warning
 *        · outside window + student: buttons disabled with "contact coach"
 *   3. Cancel is a confirm dialog with an optional reason field
 *   4. Reschedule opens a small datetime picker + reason field
 *
 * Fires an onChange callback after any successful mutation so the parent
 * can refresh the booking list / calendar.
 */
export default function BookingActions({ bookingId, onChange, compact = false }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [newStart, setNewStart] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/bookings/${bookingId}/reschedule-info`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => { if (!cancelled) setInfo(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bookingId]);

  if (loading || !info) return null;

  const isCancelled = info.status === "cancelled";
  if (isCancelled) {
    return <span className="text-[10px] uppercase tracking-wider text-[#525252]">Cancelled</span>;
  }

  const inside = info.within_cutoff;
  const canOverride = info.can_coach_override;
  const hoursText = info.hours_to_start >= 24
    ? `${(info.hours_to_start / 24).toFixed(1)}d`
    : `${Math.max(0, info.hours_to_start).toFixed(0)}h`;
  const cutoffLabel = `${info.cutoff_hours}h cutoff`;

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/cancel-any`, {
        reason,
        coach_override: !inside && canOverride,
      });
      if (data?.late_cancellation && data?.credit_message) toast.warning(data.credit_message, { duration: 8000 });
      else toast.success(data?.credit_message ? `Booking cancelled. ${data.credit_message}` : "Booking cancelled");
      setCancelOpen(false);
      setReason("");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const reschedule = async () => {
    if (!newStart) { toast.error("Pick a new start time"); return; }
    setBusy(true);
    try {
      // Convert the local datetime-input string to a UTC ISO string.
      const iso = new Date(newStart).toISOString();
      await api.post(`/bookings/${bookingId}/reschedule`, {
        new_start: iso,
        reason,
        coach_override: !inside && canOverride,
      });
      toast.success("Booking rescheduled");
      setRescheduleOpen(false);
      setReason("");
      setNewStart("");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reschedule failed");
    } finally { setBusy(false); }
  };

  const disabled = !inside && !canOverride;
  const past = !!info.is_past;

  return (
    <div className={`inline-flex items-center gap-1.5 ${compact ? "" : "flex-wrap"}`} data-testid="booking-actions">
      {past && canOverride && info.offering_id && (
        <Link to={`/offerings/${info.offering_id}`} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1" data-testid="booking-view-session-btn">
          <Eye className="w-3 h-3" /> View session
        </Link>
      )}
      {!past && <button
        onClick={() => setRescheduleOpen(true)}
        disabled={disabled}
        className={`text-[10px] uppercase tracking-wider px-2 py-1 border inline-flex items-center gap-1 transition-colors ${
          disabled
            ? "border-[#262626] text-[#404040] cursor-not-allowed"
            : inside
              ? "border-[#262626] hover:border-[#66C2FF] hover:text-[#66C2FF]"
              : "border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10"
        }`}
        title={inside ? `Reschedule (${hoursText} to start, ${cutoffLabel})` : `Inside ${cutoffLabel} — coach override`}
        data-testid="booking-reschedule-btn"
      >
        <CalendarClock className="w-3 h-3" /> Reschedule
      </button>}
      {!past && <button
        onClick={() => setCancelOpen(true)}
        disabled={disabled}
        className={`text-[10px] uppercase tracking-wider px-2 py-1 border inline-flex items-center gap-1 transition-colors ${
          disabled
            ? "border-[#262626] text-[#404040] cursor-not-allowed"
            : inside || past
              ? "border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500]"
              : "border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10"
        }`}
        title={past ? "Cancel this past session" : inside ? `Cancel (${hoursText} to start, ${cutoffLabel})` : `Inside ${cutoffLabel} — coach override`}
        data-testid="booking-cancel-btn"
      >
        <XCircle className="w-3 h-3" /> Cancel
      </button>}
      {past && (
        <span className="text-[10px] uppercase tracking-wider text-[#525252] inline-flex items-center gap-1" data-testid="booking-past-locked">
          <ShieldCheck className="w-3 h-3" /> Past session · read-only
        </span>
      )}
      {!inside && !past && (
        <span
          className={`text-[10px] uppercase tracking-wider inline-flex items-center gap-1 ${
            canOverride ? "text-[#FFD700]" : "text-[#525252]"
          }`}
          title={`Inside the ${info.cutoff_hours}h cutoff`}
        >
          {canOverride ? <AlertTriangle className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
          {canOverride ? "Override" : "Locked"}
        </span>
      )}

      {/* Cancel confirm dialog */}
      {cancelOpen && createPortal((
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !busy && setCancelOpen(false)}>
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
              <div className="text-sm font-medium">Cancel booking?</div>
              <button onClick={() => !busy && setCancelOpen(false)} className="p-1 hover:bg-[#141414]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-[#A3A3A3]">
                {past
                  ? "This session has already taken place. No attendance or feedback has been recorded, so it can still be cancelled."
                  : inside
                  ? `Starts in ${hoursText}. Well inside the ${info.cutoff_hours}h cutoff, no override needed.`
                  : `Starts in ${hoursText} — inside the ${info.cutoff_hours}h cutoff. This will use the coach override.`}
              </div>
              <div>
                <div className="label-mono text-[10px] mb-1">Reason (optional)</div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={400}
                  className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
                  placeholder="What's the reason for cancelling?"
                  data-testid="booking-cancel-reason"
                />
              </div>
            </div>
            <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2">
              <button onClick={() => setCancelOpen(false)} className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]" disabled={busy}>Keep booking</button>
              <button onClick={cancel} disabled={busy} className="text-xs px-4 py-2 bg-[#FF4500] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="booking-cancel-confirm">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Cancel booking
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Reschedule dialog */}
      {rescheduleOpen && createPortal((
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !busy && setRescheduleOpen(false)}>
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
              <div className="text-sm font-medium">Reschedule booking</div>
              <button onClick={() => !busy && setRescheduleOpen(false)} className="p-1 hover:bg-[#141414]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-[#A3A3A3]">
                {inside
                  ? `Well inside the ${info.cutoff_hours}h cutoff.`
                  : `Inside the ${info.cutoff_hours}h cutoff — this will use the coach override.`}
              </div>
              <div>
                <div className="label-mono text-[10px] mb-1">New start time</div>
                <input
                  type="datetime-local"
                  value={newStart}
                  onChange={(e) => setNewStart(e.target.value)}
                  className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
                  data-testid="booking-reschedule-time"
                />
              </div>
              <div>
                <div className="label-mono text-[10px] mb-1">Reason (optional)</div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  maxLength={400}
                  className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
                  placeholder="Why the change?"
                />
              </div>
            </div>
            <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2">
              <button onClick={() => setRescheduleOpen(false)} className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]" disabled={busy}>Never mind</button>
              <button onClick={reschedule} disabled={busy || !newStart} className="text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="booking-reschedule-confirm">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />} Reschedule
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
